import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Target, Heart, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";

const WEEKDAY_LABEL = {
  1: "Dia 1", 2: "Dia 2", 3: "Dia 3", 4: "Dia 4",
  5: "Dia 5", 6: "Dia 6", 7: "Dia 7",
};

export default function SemanaDeOracao() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(1);
  const r = useReveal();

  useEffect(() => {
    trackPageView();
    document.title = "Semana de Oração · Carlos Medeiros";
    api
      .get("/prayer-week")
      .then(({ data }) => setContent(data || null))
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, []);

  const days = content?.days || [];
  const current = days.find((d) => d.day === activeDay) || days[0];

  return (
    <div data-testid="semana-de-oracao-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#0B1E36] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o início
          </Link>

          <header ref={r} className="reveal mt-8 mb-14 text-center">
            <div className="flex items-center justify-center mb-6">
              <span className="accent-rule" />
              <span className="eyebrow">Sete dias com Deus</span>
            </div>
            <h1
              data-testid="semana-title"
              className="font-serif-display text-[#0B1E36] text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.028em] font-medium"
            >
              {content?.title || "Semana de Oração"}
            </h1>
            <p className="mt-6 text-[1.05rem] md:text-[1.15rem] text-[#6B7280] leading-[1.85] font-light max-w-2xl mx-auto">
              {content?.description ||
                "Sete dias de oração guiada. Um convite para se aproximar de Deus com fé, gratidão e entrega."}
            </p>
          </header>

          {loading && (
            <div className="text-center py-20 text-[#6B7280]" data-testid="semana-loading">
              Carregando conteúdo pastoral...
            </div>
          )}

          {!loading && days.length === 0 && (
            <div
              className="text-center py-20 text-[#6B7280]"
              data-testid="semana-empty"
            >
              O conteúdo desta semana estará disponível em breve.
            </div>
          )}

          {!loading && days.length > 0 && current && (
            <div className="grid md:grid-cols-[220px_1fr] gap-8">
              {/* Tabs / navegação */}
              <nav
                data-testid="semana-day-nav"
                className="flex md:flex-col gap-2 md:gap-3 overflow-x-auto md:overflow-visible pb-2 md:pb-0"
              >
                {days.map((d) => {
                  const isActive = d.day === activeDay;
                  return (
                    <button
                      key={d.day}
                      onClick={() => setActiveDay(d.day)}
                      data-testid={`semana-day-tab-${d.day}`}
                      className={`text-left px-4 py-3 rounded-lg border transition-all whitespace-nowrap md:whitespace-normal ${
                        isActive
                          ? "bg-[#0B1E36] text-white border-[#0B1E36] shadow-md"
                          : "bg-white text-[#0B1E36] border-[#E5E7EB] hover:border-[#C5A059]"
                      }`}
                    >
                      <div
                        className={`text-[11px] uppercase tracking-widest ${
                          isActive ? "text-[#C5A059]" : "text-[#9CA3AF]"
                        }`}
                      >
                        {WEEKDAY_LABEL[d.day] || `Dia ${d.day}`}
                      </div>
                      <div className="mt-1 text-sm font-medium leading-tight">
                        {d.title}
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Conteúdo do dia */}
              <article
                key={current.day}
                data-testid={`semana-day-content-${current.day}`}
                className="bg-white rounded-xl border border-[#E5E7EB] p-8 md:p-10 shadow-sm"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C5A059] mb-3">
                  <Calendar className="w-3.5 h-3.5" />
                  {WEEKDAY_LABEL[current.day] || `Dia ${current.day}`}
                  <span className="text-[#D1D5DB]">·</span>
                  <span className="text-[#6B7280]">{current.theme}</span>
                </div>

                <h2 className="font-serif-display text-[#0B1E36] text-3xl md:text-4xl leading-[1.1] tracking-[-0.02em] font-medium">
                  {current.title}
                </h2>

                <div className="mt-6 border-l-2 border-[#C5A059] pl-5 py-1">
                  <p
                    data-testid={`semana-verse-${current.day}`}
                    className="font-serif italic text-[#0B1E36] text-[1.05rem] leading-[1.7]"
                  >
                    “{current.verse_text}”
                  </p>
                  <p className="mt-2 text-sm text-[#6B7280] font-medium">
                    {current.verse}
                  </p>
                </div>

                <section className="mt-8">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0B1E36] mb-3">
                    <BookOpen className="w-3.5 h-3.5" />
                    Reflexão pastoral
                  </div>
                  <p
                    data-testid={`semana-prayer-guide-${current.day}`}
                    className="text-[#0B1E36] text-[1.02rem] md:text-[1.05rem] leading-[1.85] font-light whitespace-pre-line"
                  >
                    {current.prayer_guide}
                  </p>
                </section>

                {current.challenge && (
                  <section className="mt-8 bg-[#FAF6EE] rounded-lg p-6 border-l-2 border-[#C5A059]">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#8B6A2F] mb-2">
                      <Target className="w-3.5 h-3.5" />
                      Desafio do dia
                    </div>
                    <p
                      data-testid={`semana-challenge-${current.day}`}
                      className="text-[#0B1E36] text-[0.98rem] md:text-[1.02rem] leading-[1.75]"
                    >
                      {current.challenge}
                    </p>
                  </section>
                )}

                <div className="mt-10 pt-6 border-t border-[#E5E7EB] flex flex-wrap items-center justify-between gap-4">
                  <button
                    onClick={() =>
                      setActiveDay(Math.max(1, current.day - 1))
                    }
                    disabled={current.day === 1}
                    data-testid="semana-prev-day"
                    className="text-sm text-[#6B7280] hover:text-[#0B1E36] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Dia anterior
                  </button>
                  <button
                    onClick={() =>
                      setActiveDay(Math.min(days.length, current.day + 1))
                    }
                    disabled={current.day === days.length}
                    data-testid="semana-next-day"
                    className="text-sm text-[#0B1E36] hover:text-[#C5A059] disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    Próximo dia →
                  </button>
                </div>
              </article>
            </div>
          )}

          {!loading && days.length > 0 && (
            <section
              data-testid="semana-cta-oracao"
              className="mt-16 bg-[#0B1E36] text-white rounded-xl p-8 md:p-12 text-center"
            >
              <Heart className="w-8 h-8 text-[#C5A059] mx-auto mb-4" />
              <h3 className="font-serif-display text-2xl md:text-3xl mb-3">
                Deixe seu pedido de oração
              </h3>
              <p className="text-white/70 max-w-xl mx-auto mb-6 font-light leading-relaxed">
                Todo pedido é recebido com respeito e apresentado diante de Deus.
                Você não precisa caminhar sozinho.
              </p>
              <a
                href="/#oracao"
                data-testid="semana-cta-oracao-link"
                className="inline-block bg-[#C5A059] text-[#0B1E36] px-8 py-3 rounded-full font-medium hover:bg-white transition-colors"
              >
                Enviar meu pedido
              </a>
            </section>
          )}
        </div>
      </main>

      <FloatingWhatsApp />
      <Footer />
    </div>
  );
}
